import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import GridOverlay from '../GridOverlay'
import VideoTimeline from '../VideoTimeline'
import ComplianceIssueCard from './ComplianceIssueCard'

interface Asset {
  id: string
  name: string
  type: string
  size: number
  url?: string
  uploadDate: string
  lastModified: string
  thumbnailLink?: string
  webViewLink?: string
  duration?: number
  saveStatus?: 'saving' | 'saved' | 'failed' | 'unsaved'
  lastSavedAt?: string
  analysis?: {
    campaignName?: string
    fdaFilingNumber?: string
    customerType?: string
    complianceScore?: number
    summary?: {
      passed: number
      warnings: number
      failed: number
    }
    issues?: Array<{
      id?: number
      rule: string
      severity: 'critical' | 'high' | 'medium' | 'warning' | 'minor' | 'info'
      message: string
      status: 'pass' | 'fail' | 'warning' | 'not_checked'
      channel?: string
      field?: string
      section?: string
      detectedValue?: string
      expectedValue?: string
      source?: 'Built-in' | 'Custom BRD' | 'AI Analysis'
      reasoning?: string
      reviewed?: boolean
    }>
    recommendations?: string[]
    frames?: Array<{
      timestamp: number
      thumbnailUrl: string
      label?: string
      issues: Array<{ severity: string; message: string }>
    }>
  }
}

interface AssetDetailPanelProps {
  asset: Asset
  onClose: () => void
  onAssetUpdate?: (asset: Asset) => void
}

export default function AssetDetailPanel({ asset, onClose, onAssetUpdate }: AssetDetailPanelProps) {
  const [expandedIssues, setExpandedIssues] = useState<Set<number>>(new Set())
  const [debugMode, setDebugMode] = useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  // Auto-expand failed issues when asset changes
  React.useEffect(() => {
    if (asset?.analysis?.issues) {
      const failedIndexes = asset.analysis.issues
        .map((issue: any, index: number) => issue.status === 'fail' ? index : -1)
        .filter((index: number) => index !== -1)
      setExpandedIssues(new Set(failedIndexes))
    }
  }, [asset?.id])

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedIssues)
    if (expandedIssues.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedIssues(newExpanded)
  }

  const handleApplyFix = (issueId?: number) => {
    if (!asset || !issueId || !onAssetUpdate) return
    
    const updatedAsset = {
      ...asset,
      analysis: {
        ...asset.analysis,
        issues: asset.analysis?.issues?.filter((iss: any) => iss.id !== issueId) || []
      }
    }
    
    onAssetUpdate(updatedAsset)
    console.log('✅ Fix applied for', asset.name)
  }

  const handleMarkReviewed = (issueId?: number) => {
    if (!asset || !issueId || !onAssetUpdate) return
    
    const updatedAsset = {
      ...asset,
      analysis: {
        ...asset.analysis,
        issues: asset.analysis?.issues?.map((iss: any) => 
          iss.id === issueId ? { ...iss, reviewed: true } : iss
        ) || []
      }
    }
    
    onAssetUpdate(updatedAsset)
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />
      
      {/* Modal Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-700" data-testid="analysis-panel">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Analysis Details</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Asset Preview */}
          {(asset.url || asset.thumbnailLink || asset.webViewLink) && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Asset Preview</h4>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                {asset.type === 'image' ? (
                  <GridOverlay
                    imageUrl={asset.url || asset.thumbnailLink || asset.webViewLink || ''}
                    imageName={asset.name}
                    issues={asset.analysis?.issues?.filter((issue: any) => issue.coordinates) || []}
                    showGrid={false}
                  />
                ) : asset.type === 'video' ? (
                  asset.url ? (
                    asset.url.includes('drive.google.com') ? (
                      <iframe
                        src={asset.url}
                        className="w-full h-64"
                        allow="autoplay"
                        style={{ border: 'none' }}
                        onError={(e) => {
                          console.error('❌ Video iframe failed to load:', asset.url)
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    ) : (
                      <video 
                        ref={videoRef}
                        src={asset.url} 
                        controls 
                        className="w-full h-auto max-h-64"
                        onError={(e) => {
                          console.error('❌ Video element failed to load:', asset.url)
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    )
                  ) : (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                      <div className="text-center">
                        <div className="text-2xl mb-1">🎬</div>
                        <div className="text-xs">VIDEO</div>
                        <div className="text-xs text-gray-500 mt-1">No direct URL available</div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400">
                    <div className="text-center">
                      <div className="text-2xl mb-1">📄</div>
                      <div className="text-xs">{asset.type.toUpperCase()}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Video Timeline */}
              {asset.type === 'video' && asset.analysis?.frames && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Frame-by-Frame Analysis</h4>
                  <VideoTimeline
                    frames={asset.analysis.frames.map(f => ({ ...f, label: f.label || '' }))}
                    duration={asset.duration || 30}
                    onFrameClick={(timestamp) => {
                      const video = document.querySelector('video')
                      if (video) {
                        video.currentTime = timestamp
                        video.play()
                      }
                    }}
                  />
                </div>
              )}
              
              {asset.webViewLink && (
                <a 
                  href={asset.webViewLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <span>Open in Google Drive</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>
          )}
          
          {/* Asset Info */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Asset Information</h4>
            <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
              <div><span className="font-medium">Name:</span> {asset.name}</div>
              <div><span className="font-medium">Type:</span> {asset.type}</div>
              <div><span className="font-medium">Size:</span> {Math.round(asset.size / 1024)} KB</div>
              <div><span className="font-medium">Uploaded:</span> {asset.uploadDate}</div>
            </div>
          </div>

          {/* Analysis Summary */}
          {asset.analysis?.summary && (
            <div className="space-y-3" data-testid="analysis-issue-summary">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Compliance Analysis</h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Score: {asset.analysis?.complianceScore || 0}%
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2.5">
                  <div className="text-xl font-bold text-green-700 dark:text-green-400" data-testid="analysis-summary-passed">
                    {asset.analysis?.summary?.passed || 0}
                  </div>
                  <div className="text-green-600 dark:text-green-500 mt-0.5 font-medium">Passed</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2.5">
                  <div className="text-xl font-bold text-yellow-700 dark:text-yellow-400">
                    {asset.analysis?.summary?.warnings || 0}
                  </div>
                  <div className="text-yellow-600 dark:text-yellow-500 mt-0.5 font-medium">Warnings</div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2.5">
                  <div className="text-xl font-bold text-red-700 dark:text-red-400">
                    {asset.analysis?.summary?.failed || 0}
                  </div>
                  <div className="text-red-600 dark:text-red-500 mt-0.5 font-medium">Failed</div>
                </div>
              </div>
            </div>
          )}

          {/* Compliance Issues */}
          {asset.analysis?.issues && asset.analysis.issues.length > 0 && (
            <div className="space-y-3" data-testid="analysis-expandable-cards">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Compliance Checks
                </h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {asset.analysis.issues.length} rules
                </span>
              </div>
              
              <div className="space-y-2">
                {asset.analysis.issues.map((issue, index) => (
                  <ComplianceIssueCard
                    key={index}
                    issue={issue}
                    index={index}
                    expanded={expandedIssues.has(index)}
                    debugMode={debugMode}
                    onToggleExpanded={() => toggleExpanded(index)}
                    onToggleDebug={() => setDebugMode(!debugMode)}
                    onApplyFix={issue.status === 'fail' ? () => handleApplyFix(issue.id) : undefined}
                    onMarkReviewed={issue.status === 'fail' ? () => handleMarkReviewed(issue.id) : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {asset.analysis?.recommendations && asset.analysis.recommendations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Recommendations</h4>
              <div className="space-y-1 text-xs">
                {asset.analysis.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                    <span className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                    {rec}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detected Information */}
          {asset.analysis && (
            <div className="space-y-2">
              <h4 className="font-medium">Detected Information</h4>
              <div className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
                {asset.analysis.campaignName && (
                  <div><span className="font-medium">Campaign:</span> {asset.analysis.campaignName}</div>
                )}
                {asset.analysis.fdaFilingNumber && (
                  <div><span className="font-medium">FDA Filing:</span> {asset.analysis.fdaFilingNumber}</div>
                )}
                {asset.analysis.customerType && (
                  <div><span className="font-medium">Audience:</span> {asset.analysis.customerType}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

